import React, { useEffect, useState } from 'react';
import useDashboardContext from 'contexts/dashboard.context';
import { ToastMessage } from '@cokitchen/cokitchen-components';
import * as XLSX from 'xlsx';

import AddItemManually from './add-item-manually';
import { useNavigate } from 'react-router-dom';
import useInventoryStoreContext from 'contexts/inventory-store.context/context';
import { MAIN_STORE_ROUTE_KEYS } from 'utils/constants';
import { AddStoreItemType } from 'models/store-item.model';
import FormStepper from 'components/base/form-stepper';
import InventoryStorePreview from './preview';
import { toast } from 'react-toastify';
import { apiService } from 'services/base.service';
import { DefaultStoreItem } from 'contexts/inventory-store.context/provider';
import RequestDetailsWrapper from 'components/base/request-details-wrapper';
import { VendorType } from 'models/vendors.model';
import useRoleContext from 'contexts/role.context';
import { InventoryModule, RolePermission } from 'models/permission.model';

export type ActiveType = 'xls' | 'manual' | null;

const StoreItemRequest = () => {
  const navigate = useNavigate();
  const [vendorsArray, setVendorsArray] = useState([] as VendorType[]);
  const { setHideSideBar } = useDashboardContext();
  const { active, storeItemArray, setStoreItemArray, step, setStep } = useInventoryStoreContext();
  const [reviewedStoreItems, setReviewedStoreItems] = useState<number[]>(
    storeItemArray.map((_, index) => index)
  );
  const [showErrors, setShowErrors] = useState(false);
  const { can } = useRoleContext();
  const canWriteInventoryManagement = can(
    InventoryModule.InventoryManagement,
    RolePermission.Write
  );

  // const [file, setFile] = useState<File | null>(null);
  const file = null;
  const [isCreatingIngredient, setIsCreatingIngredient] = useState(false);
  const router = useNavigate();

  useEffect(() => {
    return () => {
      setHideSideBar(false);
      setStep(1);
    };
  }, [setHideSideBar]);
  const getVendors = async () => {
    try {
      const response = await apiService.get('vendors/lean', { version: 'v2' });
      setVendorsArray(response.data.results);
    } catch {
      console.log('error');
    }
  };

  const vendorId = (vendorName: string, vendorPrice: string) => {
    const vendor = {
      ...vendorsArray.find(
        (item) => item.name.trim().toLowerCase() === vendorName.trim().toLowerCase()
      ),
      price: vendorPrice ?? 0
    };
    return vendor?.id;
  };
  const readFile = () => {
    if (file) {
      const reader = new FileReader();
      reader.readAsBinaryString(file);
      reader.onload = (e: ProgressEvent<FileReader>) => {
        const data = e.target?.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        const fileDetails = workbook.SheetNames[0];

        const headers: string[] = (
          XLSX.utils.sheet_to_json(workbook.Sheets[fileDetails], { header: 1 })[0] as string[]
        ).map((header: string) => header.toLowerCase().replace(/ /g, '_'));

        let convertStoreItems = XLSX.utils.sheet_to_json(workbook.Sheets[fileDetails], {
          header: headers
        });

        convertStoreItems = convertStoreItems.slice(1);
        convertStoreItems = convertStoreItems.map((item: any) => ({
          ...item,
          vendor: vendorId(item.vendor, item.price)
        }));
        setStoreItemArray(convertStoreItems as AddStoreItemType[]);
      };
    }
  };

  useEffect(() => {
    getVendors();

    readFile();
  }, [file]);

  const hasMissingRequiredFields = (items: AddStoreItemType[]) => {
    return items.some((item) => {
      const hasName = Boolean(item.name?.trim());
      return !hasName;
    });
  };

  const runValidation = () => {
    const hasErrors = hasMissingRequiredFields(storeItemArray);
    setShowErrors(hasErrors);
    return !hasErrors;
  };

  const handleStepChange = (nextStep: number) => {
    if (nextStep > step && !runValidation()) return;
    setStep(nextStep);
  };

  const createIngredient = async () => {
    if (!canWriteInventoryManagement) return;

    const data = {
      items: storeItemArray?.map((storeItem) => {
        const mappedVendors = storeItem.vendors
          .map((vendor) => ({
            vendor_id: vendor.id,
            gram_price: Number(vendor.price) ?? 0
          }))
          .filter(
            (vendor) =>
              Boolean(vendor.vendor_id) &&
              Number.isFinite(vendor.gram_price) &&
              vendor.gram_price > 0
          );

        return {
          name: storeItem.name,
          category: storeItem.category,
          unit_type: storeItem.min_quantity_unit,
          unit_weight: Number(storeItem.unit),
          min_quantity: Number(storeItem.quantity),
          vendors: mappedVendors,
          shelf_life: storeItem.shelfLife
        };
      })
    };
    try {
      setIsCreatingIngredient(true);
      const res = await apiService.post('ingredients/csv', { data, version: 'v2' });
      if (res) {
        setStoreItemArray([DefaultStoreItem]);
        router(MAIN_STORE_ROUTE_KEYS.INVENTORY_STORE_ITEMS);
        toast(<ToastMessage message='Ingredient created successfully' type='success' />);
      }
    } catch (err: any) {
      toast(
        <ToastMessage
          message={err.response?.data?.message || 'Error creating ingredient'}
          type='error'
        />
      );
    } finally {
      setIsCreatingIngredient(false);
    }
  };

  return (
    <RequestDetailsWrapper
      title={active === 'csv' ? 'CSV upload' : 'Add new item'}
      pageTitle={
        active === 'csv'
          ? 'Track progress with procurement'
          : 'Add new items with their ingredient breakdown to the conversion list'
      }
      backLinkTitle='Return to inventory'
      withCard={false}>
      <FormStepper
        disabled={false}
        hidePrimaryAction={!canWriteInventoryManagement}
        step={step}
        title={step === 1 ? 'Add new items' : 'Review Items'}
        subtitle={step === 2 ? 'Ensure to review each item added' : ''}
        setStep={handleStepChange}
        onNextClick={runValidation}
        handleConfirmClick={() => {
          createIngredient();
        }}
        handleCancelClick={() => {
          navigate(-1);
        }}
        loading={isCreatingIngredient}>
        {step === 1 ? (
          <AddItemManually showErrors={showErrors} />
        ) : (
          <InventoryStorePreview
            reviewedStoreItems={reviewedStoreItems}
            setReviewedStoreItems={setReviewedStoreItems}
          />
        )}
      </FormStepper>
    </RequestDetailsWrapper>
  );
};

export default StoreItemRequest;
